/**
 * NST ADVISORY - Tech-Forward Main Application Logic
 * Handles Mobile Menu Drawer, Announcement Banner,
 * Interactive Human Banner Switcher & Hotspots, Pathways Tabs, FAQ Accordions,
 * and Form Submission.
 */

document.addEventListener('DOMContentLoaded', () => {
  // --------------------------------------------------------------------------
  // 1. Announcement Banner Dismissal
  // --------------------------------------------------------------------------
  const topBanner = document.getElementById('top-announcement-banner');
  const dismissBannerBtn = document.getElementById('dismiss-banner-btn');

  if (topBanner && dismissBannerBtn) {
    if (sessionStorage.getItem('nst_banner_dismissed') === 'true') {
      topBanner.style.display = 'none';
    } else {
      dismissBannerBtn.addEventListener('click', () => {
        topBanner.style.transition = 'max-height 0.3s cubic-bezier(0.2, 0, 0, 1), opacity 0.25s ease';
        topBanner.style.maxHeight = topBanner.scrollHeight + 'px';
        requestAnimationFrame(() => {
          topBanner.style.maxHeight = '0px';
          topBanner.style.opacity = '0';
          topBanner.style.overflow = 'hidden';
          setTimeout(() => {
            topBanner.style.display = 'none';
            sessionStorage.setItem('nst_banner_dismissed', 'true');
          }, 300);
        });
      });
    }
  }

  // --------------------------------------------------------------------------
  // 2. Mobile Menu Drawer Toggle
  // --------------------------------------------------------------------------
  const mobileMenuBtn = document.getElementById('mobile-menu-btn');
  const mobileMenuDrawer = document.getElementById('mobile-menu-drawer');
  const closeMobileMenuBtn = document.getElementById('close-mobile-menu-btn');
  const mobileMenuBackdrop = document.getElementById('mobile-menu-backdrop');
  let mobileMenuOpen = false;
  let mobileMenuCloseTimeout = null;
  let previousFocus = null;
  let previousBodyOverflow = '';

  function mobileMenuFocusableElements() {
    if (!mobileMenuDrawer) return [];
    const panel = mobileMenuDrawer.querySelector('.drawer-panel') || mobileMenuDrawer;
    return Array.from(panel.querySelectorAll('a[href], button, input, select, textarea, [tabindex]'))
      .filter(element => !element.disabled && element.tabIndex >= 0 && element.getClientRects().length > 0);
  }

  function focusMobileMenu() {
    const focusableElements = mobileMenuFocusableElements();
    const panel = mobileMenuDrawer.querySelector('.drawer-panel') || mobileMenuDrawer;
    if (focusableElements.length) {
      focusableElements[0].focus({ preventScroll: true });
    } else {
      if (!panel.hasAttribute('tabindex')) panel.setAttribute('tabindex', '-1');
      panel.focus({ preventScroll: true });
    }
  }

  function openMobileMenu() {
    if (!mobileMenuDrawer || mobileMenuOpen) return;
    if (mobileMenuCloseTimeout) {
      clearTimeout(mobileMenuCloseTimeout);
      mobileMenuCloseTimeout = null;
    } else {
      previousFocus = document.activeElement;
      previousBodyOverflow = document.body.style.overflow;
    }
    mobileMenuOpen = true;
    mobileMenuDrawer.classList.remove('hidden');
    requestAnimationFrame(() => {
      if (!mobileMenuOpen) return;
      mobileMenuDrawer.classList.remove('opacity-0', 'pointer-events-none');
      const panel = mobileMenuDrawer.querySelector('.drawer-panel');
      if (panel) {
        panel.classList.remove('translate-x-full');
      }
      document.body.style.overflow = 'hidden';
      if (mobileMenuBtn) mobileMenuBtn.setAttribute('aria-expanded', 'true');
      focusMobileMenu();
    });
  }

  function closeMobileMenu() {
    if (!mobileMenuDrawer || !mobileMenuOpen) return;
    mobileMenuOpen = false;
    const panel = mobileMenuDrawer.querySelector('.drawer-panel');
    if (panel) {
      panel.classList.add('translate-x-full');
    }
    mobileMenuDrawer.classList.add('opacity-0', 'pointer-events-none');
    mobileMenuCloseTimeout = setTimeout(() => {
      mobileMenuDrawer.classList.add('hidden');
      document.body.style.overflow = previousBodyOverflow;
      if (mobileMenuBtn) mobileMenuBtn.setAttribute('aria-expanded', 'false');
      const focusTarget = previousFocus && previousFocus.isConnected ? previousFocus : mobileMenuBtn;
      if (focusTarget) focusTarget.focus({ preventScroll: true });
      mobileMenuCloseTimeout = null;
    }, 250);
  }

  if (mobileMenuBtn) mobileMenuBtn.addEventListener('click', openMobileMenu);
  if (closeMobileMenuBtn) closeMobileMenuBtn.addEventListener('click', closeMobileMenu);
  if (mobileMenuBackdrop) mobileMenuBackdrop.addEventListener('click', closeMobileMenu);

  document.addEventListener('keydown', (e) => {
    if (!mobileMenuOpen) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      closeMobileMenu();
    } else if (e.key === 'Tab') {
      const focusableElements = mobileMenuFocusableElements();
      const first = focusableElements[0];
      const last = focusableElements[focusableElements.length - 1];
      if (!first) {
        e.preventDefault();
        focusMobileMenu();
      } else if (e.shiftKey && (document.activeElement === first || !focusableElements.includes(document.activeElement))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (document.activeElement === last || !focusableElements.includes(document.activeElement))) {
        e.preventDefault();
        first.focus();
      }
    }
  });

  document.addEventListener('focusin', (e) => {
    if (mobileMenuOpen && !mobileMenuDrawer.contains(e.target)) focusMobileMenu();
  });

  // --------------------------------------------------------------------------
  // 3. Header Scroll Elevation
  // --------------------------------------------------------------------------
  const mainHeader = document.getElementById('main-header');
  if (mainHeader) {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        mainHeader.classList.add('shadow-soft', 'bg-white/95', 'backdrop-blur-md');
        mainHeader.classList.remove('bg-white');
      } else {
        mainHeader.classList.remove('shadow-soft');
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
  }

  // --------------------------------------------------------------------------
  // 5. Hero Banner Slider (Image + Title Rotation)
  // --------------------------------------------------------------------------
  const heroSliderWrapper = document.getElementById('hero-slider-wrapper');
  const heroBannerImage = document.getElementById('hero-banner-image');

  // Slides are supplied by the home view as JSON (CMS banners, static fallback).
  let bannerSlides = [];
  try {
    const parsedSlides = JSON.parse(heroSliderWrapper?.dataset.slides || '[]');
    if (Array.isArray(parsedSlides) && parsedSlides.length) bannerSlides = parsedSlides;
  } catch (_) { /* leave empty; static first slide still renders server-side */ }

  let currentSlideIndex = 0;
  let sliderAutoplayInterval = null;

  const heroBannerCaption = document.getElementById('hero-banner-caption');
  const sliderDots = document.querySelectorAll('[data-slider-dot]');

  function updateHeroSlide(index) {
    if (!heroBannerImage || !bannerSlides.length) return;
    currentSlideIndex = (index + bannerSlides.length) % bannerSlides.length;
    const slide = bannerSlides[currentSlideIndex];

    // Transition image
    heroBannerImage.style.opacity = '0.3';
    heroBannerImage.style.transform = 'scale(0.99)';

    setTimeout(() => {
      heroBannerImage.src = slide.src;
      heroBannerImage.alt = slide.alt;
      if (heroBannerCaption) heroBannerCaption.textContent = slide.caption;

      heroBannerImage.style.opacity = '1';
      heroBannerImage.style.transform = 'scale(1)';
    }, 150);

    // Update indicator dots
    sliderDots.forEach((dot, i) => {
      if (i === currentSlideIndex) {
        dot.classList.add('active', 'bg-cyan-400', 'w-7');
        dot.classList.remove('bg-slate-600', 'w-2.5');
      } else {
        dot.classList.remove('active', 'bg-cyan-400', 'w-7');
        dot.classList.add('bg-slate-600', 'w-2.5');
      }
    });
  }

  function startSliderAutoplay() {
    if (sliderAutoplayInterval) clearInterval(sliderAutoplayInterval);
    sliderAutoplayInterval = setInterval(() => {
      updateHeroSlide(currentSlideIndex + 1);
    }, 6000);
  }

  function stopSliderAutoplay() {
    if (sliderAutoplayInterval) {
      clearInterval(sliderAutoplayInterval);
      sliderAutoplayInterval = null;
    }
  }

  if (heroBannerImage && bannerSlides.length > 1) {
    sliderDots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        updateHeroSlide(i);
        startSliderAutoplay();
      });
    });

    if (heroSliderWrapper) {
      heroSliderWrapper.addEventListener('mouseenter', stopSliderAutoplay);
      heroSliderWrapper.addEventListener('mouseleave', startSliderAutoplay);
    }

    startSliderAutoplay();
  }

  // --------------------------------------------------------------------------
  // 6. Interactive Hotspots on Banner
  // --------------------------------------------------------------------------
  const hotspots = document.querySelectorAll('.banner-hotspot');
  hotspots.forEach(spot => {
    const popover = spot.querySelector('.hotspot-popover');
    if (popover) {
      spot.addEventListener('mouseenter', () => {
        popover.classList.remove('hidden', 'opacity-0');
        popover.classList.add('opacity-100');
      });
      spot.addEventListener('mouseleave', () => {
        popover.classList.add('opacity-0');
        setTimeout(() => popover.classList.add('hidden'), 150);
      });
      spot.addEventListener('click', (e) => {
        e.stopPropagation();
        popover.classList.toggle('hidden');
        popover.classList.toggle('opacity-0');
      });
    }
  });

  // --------------------------------------------------------------------------
  // 7. Interactive Advisory Pathways Navigator (Home Page)
  // --------------------------------------------------------------------------
  const pathwayTabs = document.querySelectorAll('[data-pathway-tab]');
  const pathwayPanels = document.querySelectorAll('[data-pathway-panel]');

  if (pathwayTabs.length > 0 && pathwayPanels.length > 0) {
    pathwayTabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const targetId = tab.getAttribute('data-pathway-tab');

        pathwayTabs.forEach(t => {
          t.classList.remove('bg-blue-600', 'text-white', 'shadow-soft');
          t.classList.add('bg-slate-100', 'text-slate-700', 'hover:bg-slate-200');
          t.setAttribute('aria-selected', 'false');
        });
        tab.classList.remove('bg-slate-100', 'text-slate-700', 'hover:bg-slate-200');
        tab.classList.add('bg-blue-600', 'text-white', 'shadow-soft');
        tab.setAttribute('aria-selected', 'true');

        pathwayPanels.forEach(panel => {
          if (panel.getAttribute('data-pathway-panel') === targetId) {
            panel.classList.remove('hidden');
            panel.style.opacity = '0';
            panel.style.transform = 'translateY(8px)';
            requestAnimationFrame(() => {
              panel.style.transition = 'opacity 250ms cubic-bezier(0.2, 0, 0, 1), transform 250ms cubic-bezier(0.2, 0, 0, 1)';
              panel.style.opacity = '1';
              panel.style.transform = 'translateY(0)';
            });
          } else {
            panel.classList.add('hidden');
          }
        });
      });
    });
  }

  // --------------------------------------------------------------------------
  // 8. Practice & Industry Categorical Filters
  // --------------------------------------------------------------------------
  const filterButtons = document.querySelectorAll('[data-filter-btn]');
  const filterItems = document.querySelectorAll('[data-filter-item]');

  if (filterButtons.length > 0 && filterItems.length > 0) {
    filterButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        const filterVal = btn.getAttribute('data-filter-btn');

        filterButtons.forEach(b => {
          b.classList.remove('bg-blue-600', 'text-white');
          b.classList.add('bg-white', 'text-slate-700', 'border-slate-200');
        });
        btn.classList.remove('bg-white', 'text-slate-700', 'border-slate-200');
        btn.classList.add('bg-blue-600', 'text-white');

        filterItems.forEach(item => {
          const itemCategory = item.getAttribute('data-filter-category');
          if (filterVal === 'all' || itemCategory.includes(filterVal)) {
            item.classList.remove('hidden');
            item.style.opacity = '0';
            item.style.transform = 'translateY(8px)';
            requestAnimationFrame(() => {
              item.style.transition = 'opacity 200ms cubic-bezier(0.2, 0, 0, 1), transform 200ms cubic-bezier(0.2, 0, 0, 1)';
              item.style.opacity = '1';
              item.style.transform = 'translateY(0)';
            });
          } else {
            item.classList.add('hidden');
          }
        });
      });
    });
  }

  // --------------------------------------------------------------------------
  // 9. FAQ Accordions (Contact Page)
  // --------------------------------------------------------------------------
  const faqItems = document.querySelectorAll('.faq-accordion-item');
  faqItems.forEach(item => {
    const trigger = item.querySelector('.faq-trigger');
    const content = item.querySelector('.faq-content');
    const icon = item.querySelector('.faq-icon');

    if (trigger && content) {
      trigger.addEventListener('click', () => {
        const isOpen = !content.classList.contains('hidden');

        faqItems.forEach(other => {
          if (other !== item) {
            const otherContent = other.querySelector('.faq-content');
            const otherIcon = other.querySelector('.faq-icon');
            const otherTrigger = other.querySelector('.faq-trigger');
            if (otherContent) otherContent.classList.add('hidden');
            if (otherIcon) otherIcon.style.transform = 'rotate(0deg)';
            if (otherTrigger) otherTrigger.setAttribute('aria-expanded', 'false');
          }
        });

        if (isOpen) {
          content.classList.add('hidden');
          if (icon) icon.style.transform = 'rotate(0deg)';
          trigger.setAttribute('aria-expanded', 'false');
        } else {
          content.classList.remove('hidden');
          if (icon) icon.style.transform = 'rotate(180deg)';
          trigger.setAttribute('aria-expanded', 'true');
        }
      });
    }
  });

  // --------------------------------------------------------------------------
  // 10. Consultation Form Submission
  // --------------------------------------------------------------------------
  const consultationForm = document.getElementById('consultation-booking-form');
  const formSuccessAlert = document.getElementById('form-success-alert');

  if (consultationForm && formSuccessAlert) {
    consultationForm.addEventListener('submit', (e) => {
      e.preventDefault();
      const submitBtn = consultationForm.querySelector('button[type="submit"]');
      const originalText = submitBtn.innerHTML;

      submitBtn.disabled = true;
      submitBtn.innerHTML = `
        <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
        </svg>
        Securing Partner Schedule...
      `;

      setTimeout(() => {
        consultationForm.reset();
        submitBtn.disabled = false;
        submitBtn.innerHTML = originalText;
        formSuccessAlert.classList.remove('hidden');
        formSuccessAlert.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 700);
    });
  }
});
